import _ from "lodash";
import * as st from "lib/utils/socialtravel";
import BaseDecorator from "./BaseDecorator";
import ProductScheduleDecorator from "./ProductScheduleDecorator";

const coalesceOffset = (offsetSeconds) =>
  offsetSeconds === undefined || offsetSeconds === null ? null : offsetSeconds;

export default class ProductScheduleSlotDecorator extends BaseDecorator {
  static build(data) {
    return new ProductScheduleSlotDecorator({
      uuid: st.generateUuid(),
      company_id: null,
      product_id: null,
      product_variant_id: null,
      time_zone_name: null,
      active: true,
      confirmed: false,
      starts_at: null,
      ends_at: null,
      includes_time: false,
      total_stock_used: 0,
      total_stock_limit: null,
      total_stock_left: null,
      stock_type: "item",
      price_pricings: [],
      ...data,
    });
  }

  constructor(data) {
    // The relative half of the sale window rule ("z wyprzedzeniem") belongs
    // to the schedule server-side, because every slot it generates has to
    // get the same window. Mirror it onto the slot so a single form edits
    // both, and so re-opening a series shows the rule instead of the dates
    // it happened to resolve to.
    const schedule = data.product_schedule || {};
    super({
      // Null-by-default for sale window + Connectly metadata
      // so a slot that came from a path that didn't populate them
      // (BE not yet returning the field, raw constructor call, etc.)
      // doesn't crash deep form controls on undefined.
      sale_starts_at: null,
      sale_ends_at: null,
      // Coalesced against undefined rather than falsiness: an offset of 0
      // ("sprzedaż otwiera się dokładnie w terminie") is a real rule, and
      // turning it into null would show the toggle as off and drop it on the
      // next save.
      sale_starts_at_offset_seconds: coalesceOffset(schedule.sale_starts_at_offset_seconds),
      sale_ends_at_offset_seconds: coalesceOffset(schedule.sale_ends_at_offset_seconds),
      within_sale_window: null,
      connectly_slot: null,
      ...data,
      product_schedule:
        data.product_schedule &&
        (data.product_schedule instanceof ProductScheduleDecorator
          ? data.product_schedule
          : new ProductScheduleDecorator(data.product_schedule)),
    });
  }

  get product_full_name() {
    return `${this.product_name}` + (this.product_variant_name ? ` (${this.product_variant_name})` : "");
  }

  normalizeSchedule() {
    if (this.product_schedule) {
      this.product_schedule.time_duration = this.time_duration;
      this.product_schedule.default_product_pricelist_item_price = this.default_product_pricelist_item_price;

      if (!this.start_date) {
        this.product_schedule.start_dates_assignation_type = this.product_schedule.start_dates_assignation_type
          ? "normal"
          : null;
        this.product_schedule.start_dates = [];
      } else {
        if (this.product_schedule.start_dates_assignation_type === null) {
          this.product_schedule.start_dates = [this.start_date];
          this.product_schedule.start_date_recursion_from = null;
          this.product_schedule.start_date_recursion_to = null;
        } else if (this.product_schedule.start_dates_assignation_type === "normal") {
          const minStartDateTimestamp = moment
            .tz(this.start_date, moment.RUBY_DATE_FORMAT, this.time_zone_name)
            .valueOf();
          this.product_schedule.start_dates = _(this.product_schedule.start_dates)
            .concat([this.start_date])
            .map((date) => moment.tz(date, moment.RUBY_DATE_FORMAT, this.time_zone_name).valueOf())
            .filter((timestamp) => timestamp >= minStartDateTimestamp)
            .sort()
            .map((timestamp) => moment.tz(timestamp, this.time_zone_name).format(moment.RUBY_DATE_FORMAT))
            .uniq()
            .value();
          this.product_schedule.start_date_recursion_from = null;
          this.product_schedule.start_date_recursion_to = null;
        } else if (this.product_schedule.start_dates_assignation_type === "recursion") {
          this.product_schedule.start_date_recursion_from = this.start_date;
          this.product_schedule.start_date_recursion_from =
            this.product_schedule.start_date_recursion_from || this.start_date;
          this.product_schedule.start_date_recursion_to = moment
            .tz(
              Math.max(
                moment
                  .tz(
                    this.product_schedule.start_date_recursion_to || this.start_date,
                    moment.RUBY_DATE_FORMAT,
                    this.time_zone_name
                  )
                  .valueOf(),
                moment.tz(this.start_date, moment.RUBY_DATE_FORMAT, this.time_zone_name).valueOf()
              ),
              this.time_zone_name
            )
            .format(moment.RUBY_DATE_FORMAT);
          this.product_schedule.start_date_recursion_wdays = _(this.product_schedule.start_date_recursion_wdays)
            .concat([+moment.parseZone(this.starts_at).format("d")])
            .uniq()
            .value();
          this.product_schedule.normalize();
        }
      }

      if (!this.start_time || !this.includes_time) {
        this.product_schedule.start_times_assignation_type = null;
        this.product_schedule.start_times = [];
        this.product_schedule.start_time_recursion_from = null;
        this.product_schedule.start_time_recursion_to = null;
        this.product_schedule.start_time_recursion_step = null;
      } else {
        if (this.product_schedule.start_times_assignation_type === null) {
          this.product_schedule.start_times = [this.start_time];
          this.product_schedule.start_time_recursion_from = null;
          this.product_schedule.start_time_recursion_to = null;
          this.product_schedule.start_time_recursion_step = null;
        } else if (this.product_schedule.start_times_assignation_type === "normal") {
          // const minStartTimestamp = moment.tz(this.start_time, moment.TIME_FORMAT, this.time_zone_name).valueOf()
          this.product_schedule.start_times = _(this.product_schedule.start_times)
            .concat([this.start_time])
            .map((time) => moment.tz(time, moment.TIME_FORMAT, this.time_zone_name).valueOf())
            // .filter(timestamp => timestamp >= minStartTimestamp)
            .sort()
            .map((timestamp) => moment.tz(timestamp, this.time_zone_name).format(moment.TIME_FORMAT))
            .uniq()
            .value();
          this.product_schedule.start_time_recursion_from = null;
          this.product_schedule.start_time_recursion_to = null;
          this.product_schedule.start_time_recursion_step = null;
        } else if (this.product_schedule.start_times_assignation_type === "recursion") {
          // this.product_schedule.start_time_recursion_from = this.start_time
          this.product_schedule.start_time_recursion_from =
            this.product_schedule.start_time_recursion_from || this.start_time;
          this.product_schedule.start_time_recursion_to = moment
            .tz(
              Math.max(
                moment
                  .tz(
                    this.product_schedule.start_time_recursion_to || this.start_time,
                    moment.TIME_FORMAT,
                    this.time_zone_name
                  )
                  .valueOf()
                // moment.tz(this.start_time, moment.TIME_FORMAT, this.time_zone_name).valueOf(),
              ),
              this.time_zone_name
            )
            .format(moment.TIME_FORMAT);
          this.product_schedule.normalize();
        }
      }
    }
  }

  get product_schedule_id() {
    return this.product_schedule ? this.product_schedule.id : null;
  }

  get start_date() {
    return this.starts_at && moment.tz(this.starts_at, this.time_zone_name).format(moment.RUBY_DATE_FORMAT);
  }
  set start_date(date) {
    if (date) {
      this.starts_at = moment
        .tz(
          `${date}T${this.includes_time ? this.start_time : "00:00"}:00.000`,
          moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
          this.time_zone_name
        )
        .format(moment.RUBY_DATETIME_FORMAT);
    } else {
      this.starts_at = null;
    }
  }
  get start_time() {
    return this.includes_time
      ? moment.tz(this.starts_at, this.time_zone_name).format(moment.TIME_FORMAT)
      : this._start_time;
  }
  set start_time(start_time) {
    const new_includes_time = !!(start_time && this.end_time);
    if (this.includes_time && new_includes_time) {
      this.starts_at = moment
        .tz(
          `${this.start_date}T${start_time}:00.000`,
          moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
          this.time_zone_name
        )
        .format(moment.RUBY_DATETIME_FORMAT);
    } else if (this.includes_time && !new_includes_time) {
      this.starts_at = moment
        .tz(`${this.start_date}T00:00:00.000`, moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT, this.time_zone_name)
        .format(moment.RUBY_DATETIME_FORMAT);
      this._start_time = start_time;
      this._end_time = this.end_time;
      this.includes_time = false;
    } else if (!this.includes_time && new_includes_time) {
      this.starts_at = moment
        .tz(
          `${this.start_date}T${start_time}:00.000`,
          moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
          this.time_zone_name
        )
        .format(moment.RUBY_DATETIME_FORMAT);
      this.ends_at = moment
        .tz(
          `${this.end_date}T${this.end_time}:59.000`,
          moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
          this.time_zone_name
        )
        .format(moment.RUBY_DATETIME_FORMAT);
      this.includes_time = true;
    } else {
      this._start_time = start_time;
    }
  }

  get end_date() {
    return this.ends_at && moment.tz(this.ends_at, this.time_zone_name).format(moment.RUBY_DATE_FORMAT);
  }
  set end_date(date) {
    if (date) {
      this.ends_at = moment
        .tz(
          `${date}T${this.includes_time ? this.end_time : "23:59"}:59.000`,
          moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
          this.time_zone_name
        )
        .format(moment.RUBY_DATETIME_FORMAT);
    } else {
      this.ends_at = null;
    }
  }
  get end_time() {
    return this.includes_time
      ? moment.tz(this.ends_at, this.time_zone_name).format(moment.TIME_FORMAT)
      : this._end_time;
  }
  set end_time(end_time) {
    const new_includes_time = !!(this.start_time && end_time);
    if (this.includes_time && new_includes_time) {
      this.ends_at = moment
        .tz(`${this.end_date}T${end_time}:59.000`, moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT, this.time_zone_name)
        .format(moment.RUBY_DATETIME_FORMAT);
    } else if (this.includes_time && !new_includes_time) {
      this.ends_at = moment
        .tz(`${this.end_date}T23:59:59.000`, moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT, this.time_zone_name)
        .format(moment.RUBY_DATETIME_FORMAT);
      this._start_time = this.start_time;
      this._end_time = end_time;
      this.includes_time = false;
    } else if (!this.includes_time && new_includes_time) {
      this.starts_at = moment
        .tz(
          `${this.start_date}T${this.start_time}:00.000`,
          moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT,
          this.time_zone_name
        )
        .format(moment.RUBY_DATETIME_FORMAT);
      this.ends_at = moment
        .tz(`${this.end_date}T${end_time}:59.000`, moment.RUBY_DATETIME_WITHOUT_TIMEZONE_FORMAT, this.time_zone_name)
        .format(moment.RUBY_DATETIME_FORMAT);
      this.includes_time = true;
    } else {
      this._end_time = end_time;
    }
  }

  get start_date_moment() {
    return this.start_date && moment.tz(this.start_date, moment.RUBY_DATE_FORMAT, this.time_zone_name);
  }
  get end_date_moment() {
    return this.end_date && moment.tz(this.end_date, moment.RUBY_DATE_FORMAT, this.time_zone_name);
  }
  get starts_at_moment() {
    return this.starts_at && moment.parseZone(this.starts_at);
  }
  get ends_at_moment() {
    return this.ends_at && moment.parseZone(this.ends_at);
  }

  get stock_available() {
    // no limit on availability - null
    return this.total_stock_left > 0 || this.total_stock_left === null;
  }

  get overbooked() {
    return this.total_stock_limit !== null && this.total_stock_used > this.total_stock_limit;
  }

  get in_future() {
    return moment(this.starts_at).isAfter();
  }

  get orderable() {
    return this.stock_available && this.active && this.in_future;
  }

  get time_duration() {
    if (this.includes_time) {
      return moment(this.ends_at).diff(this.starts_at, "seconds");
    } else {
      return (moment(this.ends_at).diff(this.starts_at, "days") + 1) * 86400;
    }
  }

  calculatePricings({ product_variant }) {
    if (product_variant) {
      // Filter price_pricings only to those of product_pricelist_items existing in the selected variant
      this.price_pricings = this.price_pricings.filter((pricing) =>
        product_variant.product_pricelist_items.find((ppi) => ppi.id === pricing.product_pricelist_item_id)
      );

      // calculate default_product_pricelist_item_price
      const ppi = product_variant.default_product_pricelist_item;
      if (ppi) {
        const pricing = this.price_pricings.find((pricing) => pricing.product_pricelist_item_id === ppi.id);
        this.default_product_pricelist_item_price = pricing
          ? {
              value: pricing.absolute_amount_cents / 100,
              currency: ppi.price.currency,
            }
          : ppi.price;
      } else {
        this.default_product_pricelist_item_price = null;
      }
    } else {
      console.debug("ProductScheduleSlotDecorator.calculatePricings(): product_variant argument is empty");
    }
  }
}



// WEBPACK FOOTER //
// ./src/lib/decorators/ProductScheduleSlotDecorator.js